import { format, differenceInDays, differenceInMonths } from 'date-fns';
import { percentage } from '../../styles/GlobalStyles';
import { getPriceField } from '../property';

const calculatePercentageChangePrice = (property) => {
	const listingStatus = property.listingDetails.listingStatus;
	if ( listingStatus === "for_sale" || listingStatus === "pending" ) {
		if ( property.listingDetails.listSalePriceDifference === 0 ) {
			return 0;
		}

		const oldValue = property.listingDetails.saleListingPrice;
		const newValue = property.listingDetails.listingPrice;
		const percentageChange = ((newValue - oldValue) / oldValue);
		return percentageChange;
	}
	else {
		if ( property.listingDetails.saleSoldPriceDifference === 0 ) {
			return 0;
		}
		const oldValue = property.listingDetails.saleListingPrice;
		const newValue = property.listingDetails.soldListingPrice;
		const percentageChange = ((newValue - oldValue) / oldValue);
		return percentageChange;
	}
};

export const regenerateTimeline = async(properties) => {
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const listingStatus = element.listingDetails.listingStatus;
		const priceHistory = element.priceHistory;
		const priceDifference = await calculatePercentageChangePrice(element);
		const formattedPriceDifference = percentage.format(priceDifference);
		const priceDifferenceText = formattedPriceDifference === "0%" ? null : priceDifference < 0 ? `${formattedPriceDifference} below initial list price` : `+${formattedPriceDifference} above initial list price`;

		if ( listingStatus === "for_sale" || priceHistory.length === 1 ) {
			// Property is for sale

			const priorListingIndex = priceHistory.findIndex(e => e.event === "listed_for_sale");
			if ( priorListingIndex === -1 ) {
				// If the property was never listed, show empty array
				element.novationTimeline = [];
				element.novationDOM = null;
			}
			else {
				const listedDateDifference = differenceInDays(new Date(), new Date(priceHistory[priorListingIndex].date));

				const shortenedHistory = priceHistory.slice(0, priorListingIndex + 1);
				const priceCutIndex = shortenedHistory.findIndex(e => e.event === "price_change");
				
				const priceCutObject = priceCutIndex === -1 ? null : (() => {
					const priceChangeDateDifference = differenceInDays(new Date(), new Date(shortenedHistory[priceCutIndex].date));
					return {
						label: "Last price change",
						price: shortenedHistory[priceCutIndex].price,
						text: `${priceChangeDateDifference} day${priceChangeDateDifference === 1 ? "" : "s"} since price change`,
						textColour: priceChangeDateDifference < 15 ? "body-bold colour-green" : priceChangeDateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
						subText: null,
						subTextColour: null,
						abstract: false,
						date: format(new Date(shortenedHistory[priceCutIndex].date), "MMM dd, yyyy"),
						border: priceChangeDateDifference < 15 ? "novation-timeline-element-border-green" : priceChangeDateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
						difference: null
					};
				})();

				const listedObject = {
					label: "Listed",
					price: priceHistory[priorListingIndex].price,
					text: priceCutIndex !== -1 ? null : `${listedDateDifference} day${listedDateDifference === 1 ? "" : "s"} on market`,
					textColour: priceCutIndex !== -1 ? null : listedDateDifference < 15 ? "body-bold colour-green" : listedDateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
					subText: null,
					subTextColour: null,
					abstract: false,
					date: format(new Date(priceHistory[priorListingIndex].date), "MMM dd, yyyy"),
					border: priceCutIndex !== -1 ? "novation-timeline-element-border-default" : (listedDateDifference < 15 ? "novation-timeline-element-border-green" : listedDateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red"),
					difference: null
				};

				const presentObject = {
					label: "Present",
					price: getPriceField(element),
					text: null,
					textColour: null,
					subText: null,
					subTextColour: null,
					abstract: false,
					date: format(new Date(), "MMM dd, yyyy"),
					border: "novation-timeline-element-border-transparent",
					difference: priceDifferenceText
				}
				element.novationTimeline = priceCutObject !== null ? [listedObject, priceCutObject, presentObject] : [listedObject, presentObject];
				element.novationDOM = listedDateDifference;
			}
		}
		else if ( listingStatus === "pending" ) {
			// First find the listing event to ensure pending comes after it
			const priorListingIndex = priceHistory.findIndex(e => e.event === "listed_for_sale");
			
			if ( priorListingIndex === -1 ) {
				// If the property was never listed, show empty array
				element.novationTimeline = [];
				element.novationDOM = null;
			}
			else {
				// Find pending or contingent event that comes AFTER the listing
				// Check if we should look for contingent event (accepting backup offers)
				const shouldCheckContingent = element.listingDetails.acceptingBackupOffersFlag === true;
				
				// Search for pending event after the listing
				let pendingIndex = -1;
				for (let i = 0; i < priorListingIndex; i++) {
					if (priceHistory[i].event === "pending") {
						pendingIndex = i;
						break;
					}
				}
				
				// If no pending event found and we should check contingent, look for contingent event
				if (pendingIndex === -1 && shouldCheckContingent) {
					for (let i = 0; i < priorListingIndex; i++) {
						if (priceHistory[i].event === "contingent") {
							pendingIndex = i;
							break;
						}
					}
				}
				
				if ( pendingIndex === -1 ) {
					// If listingStatus is pending but no valid pending/contingent event exists after listing
					// If accepting backup offers, treat it as for_sale
					if ( shouldCheckContingent ) {
						// Treat as for_sale - use the same logic
						const listedDateDifference = differenceInDays(new Date(), new Date(priceHistory[priorListingIndex].date));

						const shortenedHistory = priceHistory.slice(0, priorListingIndex + 1);
						const priceCutIndex = shortenedHistory.findIndex(e => e.event === "price_change");
						
						const priceCutObject = priceCutIndex === -1 ? null : (() => {
							const priceChangeDateDifference = differenceInDays(new Date(), new Date(shortenedHistory[priceCutIndex].date));
							return {
								label: "Last price change",
								price: shortenedHistory[priceCutIndex].price,
								text: `${priceChangeDateDifference} day${priceChangeDateDifference === 1 ? "" : "s"} since price change`,
								textColour: priceChangeDateDifference < 15 ? "body-bold colour-green" : priceChangeDateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
								subText: null,
								subTextColour: null,
								abstract: false,
								date: format(new Date(shortenedHistory[priceCutIndex].date), "MMM dd, yyyy"),
								border: priceChangeDateDifference < 15 ? "novation-timeline-element-border-green" : priceChangeDateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
								difference: null
							};
						})();

						const listedObject = {
							label: "Listed",
							price: priceHistory[priorListingIndex].price,
							text: priceCutIndex !== -1 ? null : `${listedDateDifference} day${listedDateDifference === 1 ? "" : "s"} on market`,
							textColour: priceCutIndex !== -1 ? null : listedDateDifference < 15 ? "body-bold colour-green" : listedDateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
							subText: null,
							subTextColour: null,
							abstract: false,
							date: format(new Date(priceHistory[priorListingIndex].date), "MMM dd, yyyy"),
							border: priceCutIndex !== -1 ? "novation-timeline-element-border-default" : (listedDateDifference < 15 ? "novation-timeline-element-border-green" : listedDateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red"),
							difference: null
						};

						const presentObject = {
							label: "Present",
							price: getPriceField(element),
							text: null,
							textColour: null,
							subText: null,
							subTextColour: null,
							abstract: false,
							date: format(new Date(), "MMM dd, yyyy"),
							border: "novation-timeline-element-border-transparent",
							difference: priceDifferenceText
						}
						element.novationTimeline = priceCutObject !== null ? [listedObject, priceCutObject, presentObject] : [listedObject, presentObject];
						element.novationDOM = listedDateDifference;
					}
					else {
						// If listingStatus is pending but no valid pending/contingent event exists after listing, show empty timeline
						element.novationTimeline = [];
						element.novationDOM = null;
					}
				}
				else {
					// Property is pending - use the actual pending/contingent event
					const pendingDate = priceHistory[pendingIndex].date;
					const priorToPendingIndex = pendingIndex + 1;
					const priorToPendingEvent = priceHistory[priorToPendingIndex]?.event;
					const priorToPendingDate = priceHistory[priorToPendingIndex]?.date;
					
					if ( !priorToPendingDate ) {
						element.novationTimeline = [];
						element.novationDOM = null;
					}
					else {
						const dateDifference = differenceInDays(new Date(pendingDate), new Date(priorToPendingDate));
						const listedToPendingDateDifference = differenceInDays(new Date(pendingDate), new Date(priceHistory[priorListingIndex].date));
						
						// Price at pending
						const pendingObject = {
							label: "Pending",
							price: priceHistory[pendingIndex].price,
							text: null,
							textColour: null,
							subText: null,
							subTextColour: null,
							abstract: false,
							date: format(new Date(pendingDate), "MMM dd, yyyy"),
							border: "novation-timeline-element-border-transparent",
							difference: priceDifferenceText
						};

						// Price when originally listed
						const listedObject = {
							label: "Listed",
							price: priceHistory[priorListingIndex].price,
							text: null,
							textColour: null,
							subText: null,
							subTextColour: null,
							abstract: false,
							date: format(new Date(priceHistory[priorListingIndex].date), "MMM dd, yyyy"),
							border: "novation-timeline-element-border-default",
							difference: null
						};

						if ( priorToPendingIndex === priorListingIndex || priorToPendingEvent !== "price_change" ) {
							// If there are no price changes, just save Listed + Pending
							listedObject.text = `${dateDifference} day${dateDifference === 1 ? "" : "s"} until pending`;
							listedObject.textColour = dateDifference < 15 ? "body-bold colour-green" : dateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error";
							listedObject.abstract = true;
							listedObject.border = dateDifference < 15 ? "novation-timeline-element-border-green" : dateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red";
							if (listedToPendingDateDifference !== null) {
								listedObject.subText = `${listedToPendingDateDifference} total day${listedToPendingDateDifference === 1 ? "" : "s"} on market`;
								listedObject.subTextColour = "body-regular text-italic colour-secondary";
							}
							element.novationTimeline = [listedObject, pendingObject];
						}
						else {
							// If there is a price change, add that in too
							const priceCutObject = {
								label: "Last price change",
								price: priceHistory[priorToPendingIndex].price,
								text: `${dateDifference} day${dateDifference === 1 ? "" : "s"} until pending`,
								textColour: dateDifference < 15 ? "body-bold colour-green" : dateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
								subText: listedToPendingDateDifference !== null ? `${listedToPendingDateDifference} total day${listedToPendingDateDifference === 1 ? "" : "s"} on market` : null,
								subTextColour: "body-regular text-italic colour-secondary",
								abstract: true,
								date: format(new Date(priorToPendingDate), "MMM dd, yyyy"),
								border: dateDifference < 15 ? "novation-timeline-element-border-green" : dateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
								difference: null
							};
							element.novationTimeline = [listedObject, priceCutObject, pendingObject];
						}
						element.novationDOM = dateDifference;
					}
				}
			}
		}
		else {
			// Property is sold
			const lastSoldIndex = priceHistory.findIndex(e => e.event === "sold");
			const shortenedHistory = priceHistory.slice(lastSoldIndex, priceHistory.length);
			const secondObject = shortenedHistory.length === 1 ? null : shortenedHistory[1];
			const priorListingIndex = shortenedHistory.findIndex(e => e.event === "listed_for_sale");
			
			if ( 
				priorListingIndex === -1 || 
				shortenedHistory.length === 1 || 
				secondObject.event === "sold" || 
				secondObject.event === "removed" ||
				secondObject.event === "listed_for_rent"
			) {
				// If the property was never listed prior to being sold, 
				// then it was likely sold off-market and so there's no timeline
				element.novationTimeline = [];
				element.novationDOM = null;
			}
			else {
				const recentHistory = shortenedHistory.slice(0, priorListingIndex + 1);
				
				if ( recentHistory.length === 1 ) {
					// If there are no prior events, just show the sold price
					element.novationTimeline = [];
					element.novationDOM = null;
				}
				else if ( (recentHistory[1].event === "pending" || recentHistory[1].event === "contingent") && recentHistory.length > 2 ) {
					// If the event directly before the sale was a pending/contingent event, handle that here
					// Verify that pending comes after listing (listing should be at a higher index in recentHistory)
					// recentHistory[0] = sold, recentHistory[1] = pending, recentHistory[priorListingIndex] = listing
					// For pending to come after listing chronologically, we need priorListingIndex > 1
					if (priorListingIndex <= 1) {
						// Invalid: listing should come after pending in the array (since array is reverse chronological)
						element.novationTimeline = [];
						element.novationDOM = null;
					}
					else {
						const pendingDate = recentHistory[1].date;
						
						// Find the last price change event before pending, or use listing if no price change
						// Search backwards from index 1 (pending) to find the meaningful prior event
						let lastPriceChangeIndex = -1;
						for (let i = 2; i < recentHistory.length; i++) {
							if (recentHistory[i].event === "price_change") {
								lastPriceChangeIndex = i;
								break; // Found the last price change (most recent before pending)
							}
						}
						
						// Use last price change if found, otherwise use listing
						const meaningfulPriorIndex = lastPriceChangeIndex !== -1 ? lastPriceChangeIndex : priorListingIndex;
						const priorToPendingEvent = recentHistory[meaningfulPriorIndex].event;
						const priorToPendingDate = recentHistory[meaningfulPriorIndex].date;
						const dateDifference = differenceInDays(new Date(pendingDate), new Date(priorToPendingDate));

						const listedToPendingDateDifference = differenceInDays(new Date(pendingDate), new Date(recentHistory[priorListingIndex].date));

					const listedObject = {
						label: "Listed",
						price: recentHistory[priorListingIndex].price,
						text: null,
						textColour: null,
						subText: null,
						subTextColour: null,
						abstract: false,
						date: format(new Date(recentHistory[priorListingIndex].date), "MMM dd, yyyy"),
						border: "novation-timeline-element-border-default",
						difference: null
					}

					// Pending event + pending price
					const pendingObject = {
						label: "Pending",
						price: recentHistory[1].price,
						text: null,
						textColour: null,
						subText: null,
						subTextColour: null,
						abstract: false,
						date: format(new Date(pendingDate), "MMM dd, yyyy"),
						border: "novation-timeline-element-border-default",
						difference: null
					};

					// Sold event + sold price
					const soldObject = {
						label: "Sold",
						price: recentHistory[0].price,
						text: null,
						textColour: null,
						subText: null,
						subTextColour: null,
						abstract: false,
						date: format(new Date(recentHistory[0].date), "MMM dd, yyyy"),
						border: "novation-timeline-element-border-transparent",
						difference: priceDifferenceText
					};

					if ( priorToPendingEvent === "price_change" ) {
						// If there were price changes before the pending event, handle that here

						const priceChangeObject = {
							label: "Last price change",
							price: recentHistory[meaningfulPriorIndex].price,
							text: `${dateDifference} day${dateDifference === 1 ? "" : "s"} until pending`,
							textColour: dateDifference < 15 ? "body-bold colour-green" : dateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
							subText: `${listedToPendingDateDifference} total day${listedToPendingDateDifference === 1 ? "" : "s"} on market`,
							subTextColour: "body-regular text-italic colour-secondary",
							abstract: true,
							date: format(new Date(priorToPendingDate), "MMM dd, yyyy"),
							border: dateDifference < 15 ? "novation-timeline-element-border-green" : dateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
							difference: null
						};

						element.novationTimeline = [listedObject, priceChangeObject, pendingObject, soldObject];

					}
					else {
						// No price changes. just Listed -> Pending -> Sold

						listedObject.text = `${dateDifference} day${dateDifference === 1 ? "" : "s"} until pending`;
						listedObject.textColour = dateDifference < 15 ? "body-bold colour-green" : dateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error";
						listedObject.abstract = true;
						listedObject.border = dateDifference < 15 ? "novation-timeline-element-border-green" : dateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red";
						listedObject.subText = `${listedToPendingDateDifference} total day${listedToPendingDateDifference === 1 ? "" : "s"} on market`;
						listedObject.subTextColour = "body-regular text-italic colour-secondary";

						element.novationTimeline = [listedObject, pendingObject, soldObject];
					}
					element.novationDOM = dateDifference;
				}
				}
				else {
					// Go straight from For Sale to Sold
					const priorToSoldEvent = recentHistory[1].event;
					const priorToSoldDate = recentHistory[1].date;
					const dateDifference = differenceInDays(new Date(recentHistory[0].date), new Date(priorToSoldDate));
					const abstractObject = {
						label: priorToSoldEvent === "price_change" ? "Last price change" : "Listed",
						price: recentHistory[1].price,
						text: `${dateDifference} day${dateDifference === 1 ? "" : "s"} until sold`,
						textColour: dateDifference < 45 ? "body-bold colour-green" : dateDifference < 91 ? "body-bold colour-orange" : "body-bold colour-error",
						subText: null,
						subTextColour: null,
						abstract: true,
						date: format(new Date(priorToSoldDate), "MMM dd, yyyy"),
						border: dateDifference < 45 ? "novation-timeline-element-border-green" : dateDifference < 91 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
						difference: null
					};

					const soldObject = {
						label: "Sold",
						price: recentHistory[0].price,
						text: null,
						textColour: null,
						subText: null,
						subTextColour: null,
						abstract: false,
						date: format(new Date(recentHistory[0].date), "MMM dd, yyyy"),
						border: "novation-timeline-element-border-transparent",
						difference: priceDifferenceText
					};

					element.novationTimeline = [abstractObject, soldObject];
					element.novationDOM = dateDifference;
				}
			}
		}
	};

	return properties;
};

export const generateNovationScores = async(properties) => {
	const novationGroups = [];
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const overallScore = element.compDetails.overallScore;
		if ( overallScore < 0.8 ) {
			continue;
		}

		const listingDetails = element.listingDetails;
		if ( listingDetails.listingStatus !== "sold" ) {
			continue;
		}

		const priceHistory = element.priceHistory;
		if ( priceHistory === undefined ) {
			continue;
		}

		// Original listing price
		const originalPrice = listingDetails.saleListingPrice;

		// Final sold price
		const soldPrice = listingDetails.soldListingPrice;

		// How many times was the price changed WHILST being actively for sale
		// NB - Even if there are 0 price changes, the final sold price could still be different 
		// if there was a bidding war, but the MLS simply was never updated
		const totalPriceChanges = listingDetails.totalPriceChanges;

		const lastPendingIndex = priceHistory.findIndex(e => e.event === "pending");
		const lastPendingObject = lastPendingIndex === -1 ? null : priceHistory[lastPendingIndex];
		const lastPendingDate = lastPendingObject === null ? null : lastPendingObject.date;
		const lastPendingDateDifference = lastPendingDate === null ? null : differenceInMonths(new Date(), new Date(lastPendingDate));

		if ( (lastPendingIndex === -1) || (lastPendingDateDifference > 12) || ((lastPendingIndex + 1) === priceHistory.length) || (priceHistory[lastPendingIndex + 1].event === "removed") || (priceHistory[lastPendingIndex + 1].event === "listed_for_rent") ) {
			// If there are no pendings or the last pending was over 1 year ago, just focus on time to go sold.
			// Also if the last pending event has no prior events, just focus on sold

			// Excluding comps where no sold event exists, or if that's there are no prior events to it being sold
			const lastSoldIndex = priceHistory.findIndex(e => e.event === "sold");
			if ( lastSoldIndex === -1 || (lastSoldIndex + 1 === priceHistory.length) ) {
				continue;
			}

			// If the event prior to the sale was a listing for rent, or a sale, or a removal, then this comp can't inform us
			// on how long it took to go pending
			const priorToSaleEvent = priceHistory[lastSoldIndex + 1].event;
			if ( priorToSaleEvent === "listed_for_rent" || priorToSaleEvent === "sold" || priorToSaleEvent === "removed" ) {
				continue;
			}

			const lastSoldObject = priceHistory[lastSoldIndex];
			const eventBeforeSold = priceHistory[lastSoldIndex + 1];
			const dateDifferenceFromSold = differenceInDays(new Date(lastSoldObject.date), new Date(eventBeforeSold.date));
			const relevantPriceHistory = priceHistory.slice(lastSoldIndex, (lastSoldIndex + 2));
			const newObject = {
				daysToPending: null,
				zpid: element.uniqueIds.zillowId,
				priceHistory: relevantPriceHistory,
				condition: element.compDetails.propertyCondition,
				pendingPrice: null,
				pending: false,
				soldPrice: soldPrice,
				originalPrice: originalPrice,
				totalPriceChanges: totalPriceChanges,
				daysToSale: dateDifferenceFromSold,
				overallScore: element.compDetails.overallScore,
				link: element.propertyUrls.zillowUrl
			};
			novationGroups.push(newObject);
		}
		else {
			const eventBeforePending = priceHistory[lastPendingIndex + 1];
			const dateDifferenceFromPending = differenceInDays(new Date(lastPendingDate), new Date(eventBeforePending.date));

			const lastSoldIndex = priceHistory.findIndex(e => e.event === "sold");
			const relevantPriceHistory = priceHistory.slice(lastSoldIndex, (lastPendingIndex + 2));
			const newObject = {
				daysToPending: dateDifferenceFromPending,
				zpid: element.uniqueIds.zillowId,
				priceHistory: relevantPriceHistory,
				condition: element.compDetails.propertyCondition,
				pendingPrice: lastPendingObject.price,
				pending: true,
				soldPrice: soldPrice,
				originalPrice: originalPrice,
				totalPriceChanges: totalPriceChanges,
				daysToSale: null,
				overallScore: element.compDetails.overallScore,
				link: element.propertyUrls.zillowUrl
			}
			novationGroups.push(newObject);

			// Subject property = 163057470

			// ZPID = 45096204...property started at $465k -> $415k
			// ZPID = 45095602...property started at $350k -> $435k

			// if ( newObject.zpid === "45096204" || newObject.zpid === "45095602" ) {
			// 	console.log(newObject)
			// }
		}

		// Map comps by time to go pending
		// 1. Price points for 1 month or less
		// 2. Price points for 2 monts or less
		// 2. Price points for 3 monts or less
		// 2. Price points for 4 monts or less
		// 2. Price points for 5 monts or less
		// 2. Price points for 6 monts or less
	};

	const novationScores = [];
	for (let index = 0; index < novationGroups.length; index++) {
		const element = novationGroups[index];
		const condition = element.condition;
		const conditionIndex = novationScores.findIndex(e => e.condition === condition);
		if ( conditionIndex === -1 ) {
			const newObject = {
				condition: condition,
				group: [element]
			};
			novationScores.push(newObject);
		}
		else {
			novationScores[conditionIndex].group.push(element);
		}
	};

	novationScores.sort((a, b) => a.condition.localeCompare(b.condition));
	return novationScores;
};

